// ==================== ABOUT US PAGE COMPONENT WITH EDIT FORM ====================

import React, { useEffect, useState, useContext } from "react";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData, postData, serverURL } from "../../../../services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const SaveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const AboutIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6" />
    <path d="M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
  </svg>
);

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const FileIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const Aboutus = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    page_title: "",
    page_description: "",
    page_thumbnail: null,
  });
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("aboutus/aboutus");
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        setPage(response.data);
        setEditForm({
          page_title: response.data.page_title || "",
          page_description: response.data.page_description || "",
          page_thumbnail: null,
        });
      } else {
        setError("About us page not found");
      }
    } catch (e) {
      console.error("Error fetching about us:", e);
      setError("Failed to load about us page. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditForm({
      page_title: page?.page_title || "",
      page_description: page?.page_description || "",
      page_thumbnail: null,
    });
    setThumbnailPreview(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      page_title: page?.page_title || "",
      page_description: page?.page_description || "",
      page_thumbnail: null,
    });
    setThumbnailPreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm(prev => ({ ...prev, page_thumbnail: file }));
      const reader = new FileReader();
      reader.onloadend = () => setThumbnailPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append("id", page.id);
      formData.append("page_title", editForm.page_title);
      formData.append("page_description", editForm.page_description);
      if (editForm.page_thumbnail) {
        formData.append("page_thumbnail", editForm.page_thumbnail);
      }

      // ✅ Fixed endpoint: was privacypolicy/updatepage, now aboutus/updatepage
      const response = await postData("aboutus/updatepage", formData, true);
      
      if (response?.status) {
        await fetchPage();
        setIsEditing(false);
        alert("✅ About us page updated successfully!");
      } else {
        alert("Failed to update. Please try again.");
      }
    } catch (error) {
      console.error("Update error:", error);
      alert("Error updating about us page");
    } finally {
      setUpdating(false);
    }
  };

  // ─── Loading State ─────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: theme.background,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "56px",
              height: "56px",
              border: `3px solid ${theme.border}`,
              borderTopColor: theme.primary,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <p style={{ color: theme.textMuted, fontSize: "14px", margin: 0 }}>
            Loading about us page...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // ─── Error State ───────────────────────────────────────────────
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: theme.background,
          padding: "20px",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div
          style={{
            textAlign: "center",
            backgroundColor: theme.surface,
            padding: "48px",
            borderRadius: "20px",
            border: `1px solid ${theme.border}`,
            maxWidth: "440px",
            boxShadow: `0 20px 60px ${theme.primary}20`,
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: `${theme.error || theme.danger}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              color: theme.error || theme.danger,
            }}
          >
            <AboutIcon />
          </div>
          <h2 style={{ color: theme.textPrimary, marginBottom: "8px", fontSize: "20px", fontWeight: 700 }}>
            About Us Page Not Found
          </h2>
          <p style={{ color: theme.textMuted, marginBottom: "24px", fontSize: "14px" }}>
            {error}
          </p>
          <button
            onClick={fetchPage}
            style={{
              padding: "10px 24px",
              backgroundColor: theme.primary,
              color: "white",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "13px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: `0 4px 12px ${theme.primary}40`,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}40`;
            }}
          >
            <RefreshIcon /> Try Again
          </button>
        </div>
        <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    );
  }

  // ─── Main Styles ───────────────────────────────────────────────
  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    backgroundColor: theme.surfaceLighter,
    border: `1px solid ${theme.border}`,
    borderRadius: "10px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s ease",
    boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "12px",
    fontWeight: 600,
    color: theme.textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 2000px; } }

        .about-us-content {
          word-wrap: break-word;
          animation: fadeIn 0.4s ease-out;
        }
        .about-us-content h1,
        .about-us-content h2,
        .about-us-content h3,
        .about-us-content h4 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          font-weight: 700;
          color: ${theme.textPrimary};
        }
        .about-us-content h1 {
          font-size: 2rem;
          margin-top: 0;
        }
        .about-us-content h2 {
          font-size: 1.5rem;
          border-left: 4px solid ${theme.primary};
          padding-left: 1rem;
        }
        .about-us-content h3 {
          font-size: 1.25rem;
        }
        .about-us-content h4 {
          font-size: 1.1rem;
          color: ${theme.textPrimary};
        }
        .about-us-content p {
          line-height: 1.8;
          margin-bottom: 1rem;
          color: ${theme.textSecondary};
          font-size: 14px;
        }
        .about-us-content ul,
        .about-us-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: ${theme.textSecondary};
        }
        .about-us-content li {
          margin: 0.5rem 0;
          line-height: 1.7;
          font-size: 14px;
        }
        .about-us-content hr {
          margin: 2rem 0;
          border: none;
          height: 1px;
          background: ${theme.border};
        }
        .about-us-content blockquote {
          margin: 1rem 0;
          padding: 1rem 1.5rem;
          background: ${theme.surfaceLighter};
          border-left: 4px solid ${theme.primary};
          border-radius: 10px;
          font-style: italic;
          color: ${theme.textSecondary};
        }
        .about-us-content a {
          color: ${theme.primary};
          text-decoration: none;
          font-weight: 500;
        }
        .about-us-content a:hover {
          text-decoration: underline;
        }
        .about-us-content strong {
          color: ${theme.textPrimary};
          font-weight: 700;
        }
        .about-us-content code {
          background: ${theme.surfaceLighter};
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Monaco', monospace;
          font-size: 13px;
          color: ${theme.primary};
        }
        .about-us-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          border-radius: 10px;
          overflow: hidden;
        }
        .about-us-content th,
        .about-us-content td {
          border: 1px solid ${theme.border};
          padding: 0.75rem;
          text-align: left;
          color: ${theme.textPrimary};
          font-size: 13px;
        }
        .about-us-content th {
          background: ${theme.surfaceLighter};
          font-weight: 700;
          color: ${theme.primary};
        }
        @media (max-width: 768px) {
          .about-us-content h1 { font-size: 1.5rem; }
          .about-us-content h2 { font-size: 1.25rem; }
          .about-us-content h3 { font-size: 1.1rem; }
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          backgroundColor: theme.background,
          padding: "40px 20px",
          fontFamily: "'DM Sans', sans-serif",
          transition: "background 0.3s ease",
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            backgroundColor: theme.surface,
            borderRadius: "20px",
            border: `1px solid ${theme.border}`,
            overflow: "hidden",
            boxShadow: `0 20px 60px ${theme.primary}15`,
          }}
        >
          {/* ─── Top Action Bar ───────────────────────────────────── */}
          <div
            style={{
              padding: "16px 32px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `1px solid ${theme.border}`,
              backgroundColor: theme.surfaceLighter,
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: `${theme.primary}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.primary,
                }}
              >
                <AboutIcon />
              </div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: 700, color: theme.textPrimary }}>
                  About Us Page Management
                </div>
                <div style={{ fontSize: "11px", color: theme.textMuted }}>
                  CMS Page Configuration
                </div>
              </div>
            </div>

            {!isEditing ? (
              <button
                onClick={handleEditClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  backgroundColor: theme.primary,
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  boxShadow: `0 4px 12px ${theme.primary}40`,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}40`;
                }}
              >
                <EditIcon /> Edit About Us
              </button>
            ) : (
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={handleCancelEdit}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "10px 18px",
                    backgroundColor: "transparent",
                    color: theme.textPrimary,
                    border: `1px solid ${theme.border}`,
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 500,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.surface;
                    e.currentTarget.style.borderColor = theme.error || theme.danger;
                    e.currentTarget.style.color = theme.error || theme.danger;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.color = theme.textPrimary;
                  }}
                >
                  <CloseIcon /> Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={updating}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 20px",
                    backgroundColor: updating ? theme.textMuted : theme.success,
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: updating ? "not-allowed" : "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                    boxShadow: updating ? "none" : `0 4px 12px ${theme.success}40`,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!updating) {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = `0 6px 16px ${theme.success}60`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = updating ? "none" : `0 4px 12px ${theme.success}40`;
                  }}
                >
                  {updating ? (
                    <>
                      <div
                        style={{
                          width: "14px",
                          height: "14px",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "white",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <SaveIcon /> Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* ─── Edit Form ─────────────────────────────────────────── */}
          {isEditing && (
            <div
              style={{
                padding: "32px",
                backgroundColor: theme.surfaceLighter,
                borderBottom: `1px solid ${theme.border}`,
                animation: "slideDown 0.3s ease-out",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "24px",
                  padding: "12px 16px",
                  background: `${theme.warning}15`,
                  border: `1px solid ${theme.warning}30`,
                  borderRadius: "10px",
                }}
              >
                <span style={{ fontSize: "18px" }}>⚡</span>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 700, color: theme.warning }}>
                    Edit Mode Active
                  </div>
                  <div style={{ fontSize: "11px", color: theme.textMuted }}>
                    Make your changes and click "Save Changes" to update the about us page
                  </div>
                </div>
              </div>
              
              {/* Title Input */}
              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>
                  Page Title <span style={{ color: theme.error || theme.danger }}>*</span>
                </label>
                <input
                  type="text"
                  name="page_title"
                  value={editForm.page_title}
                  onChange={handleInputChange}
                  placeholder="Enter page title"
                  style={inputStyle}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Thumbnail Upload */}
              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>Thumbnail Image</label>
                <div
                  style={{
                    border: `2px dashed ${theme.border}`,
                    borderRadius: "12px",
                    padding: "20px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    background: theme.surface,
                  }}
                  onClick={() => document.getElementById('thumbnail-input').click()}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.background = `${theme.primary}08`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.background = theme.surface;
                  }}
                >
                  <input
                    id="thumbnail-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <div style={{ color: theme.primary, marginBottom: "8px", display: "flex", justifyContent: "center" }}>
                    <UploadIcon />
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: theme.textPrimary, marginBottom: "4px" }}>
                    Click to upload thumbnail
                  </div>
                  <div style={{ fontSize: "11px", color: theme.textMuted }}>
                    PNG, JPG, WebP up to 10MB
                  </div>
                </div>

                {(thumbnailPreview || page?.page_thumbnail) && (
                  <div style={{ marginTop: "16px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: theme.textMuted, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Preview
                    </div>
                    <img
                      src={thumbnailPreview || `${serverURL}/images/${page.page_thumbnail}`}
                      alt="Thumbnail preview"
                      style={{
                        width: "160px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "10px",
                        border: `1px solid ${theme.border}`,
                        boxShadow: `0 4px 12px ${theme.primary}15`,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Description Textarea */}
              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>
                  Page Content <span style={{ color: theme.textMuted, fontWeight: 400, textTransform: "none", letterSpacing: "0" }}>(HTML supported)</span>
                </label>
                <textarea
                  name="page_description"
                  value={editForm.page_description}
                  onChange={handleInputChange}
                  rows={18}
                  placeholder="Enter HTML content for about us page..."
                  style={{
                    ...inputStyle,
                    fontFamily: "'Monaco', 'Courier New', monospace",
                    fontSize: "13px",
                    resize: "vertical",
                    minHeight: "300px",
                    lineHeight: 1.6,
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
                <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "6px", textAlign: "right" }}>
                  {editForm.page_description.length} characters
                </div>
              </div>
            </div>
          )}

          {/* ─── Thumbnail Image (Display Mode) ────────────────────── */}
          {!isEditing && page?.page_thumbnail && (
            <div
              style={{
                position: "relative",
                height: "320px",
                overflow: "hidden",
              }}
            >
              <img
                src={`${serverURL}/images/${page.page_thumbnail}`}
                alt={page.page_title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentElement.style.height = "auto";
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.4), transparent)",
                  padding: "60px 40px 32px",
                }}
              >
                <h1
                  style={{
                    color: "white",
                    fontSize: "2.5rem",
                    margin: 0,
                    fontWeight: 800,
                    textShadow: "0 2px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  {page?.page_title}
                </h1>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "rgba(255,255,255,0.9)",
                    marginTop: "12px",
                    fontSize: "13px",
                    background: "rgba(255,255,255,0.15)",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <CalendarIcon /> Last updated: {formatDate(page?.updated_at)}
                </div>
              </div>
            </div>
          )}

          {/* ─── No Thumbnail - Gradient Header ────────────────────── */}
          {!isEditing && !page?.page_thumbnail && (
            <div
              style={{
                background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primary}cc 50%, #764ba2 100%)`,
                padding: "64px 40px",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative Circles */}
              <div
                style={{
                  position: "absolute",
                  top: "-50px",
                  right: "-50px",
                  width: "200px",
                  height: "200px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.1)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-80px",
                  left: "-80px",
                  width: "250px",
                  height: "250px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                }}
              />

              <div
                style={{
                  width: "88px",
                  height: "88px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <span style={{ color: "white" }}>
                  <AboutIcon />
                </span>
              </div>
              <h1
                style={{
                  color: "white",
                  margin: 0,
                  fontSize: "2.5rem",
                  fontWeight: 800,
                  position: "relative",
                  zIndex: 1,
                  textShadow: "0 2px 12px rgba(0,0,0,0.2)",
                }}
              >
                {page?.page_title}
              </h1>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: "rgba(255,255,255,0.9)",
                  marginTop: "16px",
                  fontSize: "13px",
                  background: "rgba(255,255,255,0.15)",
                  padding: "6px 16px",
                  borderRadius: "20px",
                  backdropFilter: "blur(8px)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <CalendarIcon /> Last updated: {formatDate(page?.updated_at)}
              </div>
            </div>
          )}

          {/* ─── Content Section ───────────────────────────────────── */}
          <div style={{ padding: "48px" }}>
            {!isEditing ? (
              <div
                className="about-us-content"
                dangerouslySetInnerHTML={{ __html: page?.page_description || "No content available" }}
              />
            ) : (
              <div
                style={{
                  padding: "24px",
                  backgroundColor: theme.surfaceLighter,
                  borderRadius: "12px",
                  textAlign: "center",
                  color: theme.textMuted,
                  border: `1px dashed ${theme.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "20px" }}>⚡</span>
                <span style={{ fontSize: "13px", fontWeight: 500 }}>
                  Edit mode active — Make your changes in the form above and click Save
                </span>
              </div>
            )}

            {/* ─── Footer Information ────────────────────────────────── */}
            {!isEditing && (
              <div
                style={{
                  marginTop: "48px",
                  paddingTop: "24px",
                  borderTop: `1px solid ${theme.border}`,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 16px",
                    background: theme.surfaceLighter,
                    borderRadius: "10px",
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: `${theme.primary}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.primary,
                    }}
                  >
                    <FileIcon />
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Page ID
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: theme.textPrimary, fontFamily: "monospace" }}>
                      #{page?.id}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 16px",
                    background: theme.surfaceLighter,
                    borderRadius: "10px",
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: `${theme.primary}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.primary,
                    }}
                  >
                    <CalendarIcon />
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Created
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: theme.textPrimary }}>
                      {formatDate(page?.created_at)}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px 16px",
                    background: theme.surfaceLighter,
                    borderRadius: "10px",
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: page?.status === 1 ? `${theme.success}15` : `${theme.error || theme.danger}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: page?.status === 1 ? theme.success : (theme.error || theme.danger),
                    }}
                  >
                    <CheckCircleIcon />
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Status
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: page?.status === 1 ? theme.success : (theme.error || theme.danger) }}>
                      {page?.status === 1 ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ─── Copyright Footer ──────────────────────────────────── */}
          {!isEditing && (
            <div
              style={{
                padding: "20px 48px",
                borderTop: `1px solid ${theme.border}`,
                backgroundColor: theme.surfaceLighter,
                fontSize: "12px",
                color: theme.textMuted,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              <AboutIcon /> © {new Date().getFullYear()} Zaptodoor. All rights reserved.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Aboutus;